import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { PlayArrow, Pause, ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';

const CarouselContainer = styled.div`
  margin: 60px 0;
  position: relative;
`;

const GalleryWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background: black;
  position: relative;
  aspect-ratio: 16/9;
  max-height: 700px;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.objectFit || 'cover'};
  object-position: ${props => props.objectPosition || 'center'};
  display: block;
`;

const ControlsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.3) 100%);

  ${GalleryWrapper}:hover & {
    opacity: 1;
  }
`;

const NavButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.1);
  }
`;

const Counter = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  font-family: var(--font-sans);
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  width: 100%;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: white;
  width: ${props => props.progress}%;
  transition: width 0.3s linear;
`;

const MediaCarousel = ({ media, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const currentItem = media[currentIndex];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % media.length);
    }, interval);

    return () => clearInterval(timer);
  }, [isAutoPlaying, media.length, interval]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % media.length);
    setIsAutoPlaying(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
    setIsAutoPlaying(false);
  };

  if (!media || media.length === 0) return null;

  return (
    <CarouselContainer>
      <GalleryWrapper>
        <CarouselImage
          src={currentItem.src}
          objectFit={currentItem.objectFit}
          objectPosition={currentItem.objectPosition}
          alt="Event gallery"
        />

        <ControlsOverlay>
          <NavButton onClick={handlePrev}>
            <ArrowBackIosNew style={{ fontSize: '20px' }} />
          </NavButton>

          <NavButton onClick={() => setIsAutoPlaying(!isAutoPlaying)}>
            {isAutoPlaying ? <Pause /> : <PlayArrow />}
          </NavButton>

          <NavButton onClick={handleNext}>
            <ArrowForwardIos style={{ fontSize: '20px' }} />
          </NavButton>

          <Counter>
            {currentIndex + 1} / {media.length}
          </Counter>

          <ProgressBar>
            <ProgressFill progress={((currentIndex + 1) / media.length) * 100} />
          </ProgressBar>
        </ControlsOverlay>
      </GalleryWrapper>
    </CarouselContainer>
  );
};

export default MediaCarousel;